import React from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams, useNavigate } from "react-router-dom";
import { getJob } from "../../../data/requests/job-requests";
import { AppLayout } from "../shared/layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/use-toast";
import { ArrowLeft, Download, ExternalLink, Clock, CheckCircle, XCircle, AlertCircle } from "lucide-react";

export function JobDetailScreen() {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data: job, isLoading } = useQuery({
    queryKey: ["job", id],
    queryFn: () => getJob(id).then(res => res.data),
  });

  if (isLoading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-lg">Loading...</div>
        </div>
      </AppLayout>
    );
  }

  if (!job) {
    return (
      <AppLayout>
        <div className="text-center">
          <p>Job not found</p>
          <Button onClick={() => navigate("/app/jobs")} className="mt-4">
            Back to Jobs
          </Button>
        </div>
      </AppLayout>
    );
  }

  const getStatusBadge = (status) => {
    const statusConfig = {
      PENDING: { variant: "secondary", icon: Clock, label: "Pending" },
      PROCESSING: { variant: "default", icon: AlertCircle, label: "Processing" },
      COMPLETED: { variant: "success", icon: CheckCircle, label: "Completed" },
      FAILED: { variant: "destructive", icon: XCircle, label: "Failed" },
      CANCELLED: { variant: "secondary", icon: XCircle, label: "Cancelled" },
    };

    const config = statusConfig[status] || statusConfig.PENDING;
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className="text-base px-3 py-1">
        <Icon className="mr-1 h-4 w-4" />
        {config.label}
      </Badge>
    );
  };

  return (
    <AppLayout>
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate("/app/jobs")}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-3xl font-bold">Job #{job.id}</h1>
          {getStatusBadge(job.status)}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Job Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-sm text-gray-500">Template</p>
                <p className="font-medium">{job.template?.name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Created</p>
                <p className="font-medium">{new Date(job.createdAt).toLocaleString()}</p>
              </div>
              {job.completedAt && (
                <div>
                  <p className="text-sm text-gray-500">Completed</p>
                  <p className="font-medium">{new Date(job.completedAt).toLocaleString()}</p>
                </div>
              )}
              {job.processingTime && (
                <div>
                  <p className="text-sm text-gray-500">Processing Time</p>
                  <p className="font-medium">{(job.processingTime / 1000).toFixed(2)}s</p>
                </div>
              )}
              {job.downloadCount > 0 && (
                <div>
                  <p className="text-sm text-gray-500">Downloads</p>
                  <p className="font-medium">{job.downloadCount}</p>
                </div>
              )}
            </CardContent>
          </Card>

          {job.errorMessage && (
            <Card className="border-red-200">
              <CardHeader>
                <CardTitle className="text-red-600">Error</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm">{job.errorMessage}</p>
              </CardContent>
            </Card>
          )}

          {job.qrCode && (
            <Card>
              <CardHeader>
                <CardTitle>Download Link</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-500 mb-2">Share this link with customers:</p>
                <div className="flex gap-2">
                  <code className="flex-1 p-2 bg-gray-100 rounded text-sm break-all">
                    {job.qrCode}
                  </code>
                  <Button
                    size="sm"
                    onClick={() => {
                      navigator.clipboard.writeText(job.qrCode);
                      toast({ title: "Copied to clipboard" });
                    }}
                  >
                    Copy
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card>
            <CardHeader>
              <CardTitle>Input Image</CardTitle>
            </CardHeader>
            <CardContent>
              <img
                src={job.inputImageUrl}
                alt="Input"
                className="w-full rounded-lg"
              />
              <Button
                className="w-full mt-2"
                variant="outline"
                size="sm"
                onClick={() => window.open(job.inputImageUrl, "_blank")}
              >
                <ExternalLink className="mr-2 h-4 w-4" />
                Open
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Template</CardTitle>
            </CardHeader>
            <CardContent>
              <img
                src={job.template?.imageUrl}
                alt="Template"
                className="w-full rounded-lg"
              />
              <Button
                className="w-full mt-2"
                variant="outline"
                size="sm"
                onClick={() => window.open(job.template?.imageUrl, "_blank")}
              >
                <ExternalLink className="mr-2 h-4 w-4" />
                Open
              </Button>
            </CardContent>
          </Card>

          {job.outputImageUrl && (
            <Card>
              <CardHeader>
                <CardTitle>Output Image</CardTitle>
              </CardHeader>
              <CardContent>
                <img
                  src={job.outputImageUrl}
                  alt="Output"
                  className="w-full rounded-lg"
                />
                <Button
                  className="w-full mt-2"
                  onClick={() => window.open(job.outputImageUrl, "_blank")}
                >
                  <Download className="mr-2 h-4 w-4" />
                  Download
                </Button>
              </CardContent>
            </Card>
          )}
        </div>

        {job.webhookLogs && job.webhookLogs.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Webhook Logs</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {job.webhookLogs.map((log) => (
                  <div key={log.id} className="p-3 border rounded text-sm">
                    <div className="flex justify-between items-center">
                      <span className="font-medium">{log.requestMethod} {log.requestUrl}</span>
                      <Badge variant={log.success ? "success" : "destructive"}>
                        {log.success ? "Success" : "Failed"}
                      </Badge>
                    </div>
                    <p className="text-gray-500 text-xs mt-1">
                      {new Date(log.createdAt).toLocaleString()} • {log.duration}ms
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </AppLayout>
  );
}

